import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Award, BookOpen, Clock, Settings2, Users } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr, fmtDate, ROLE_LABELS } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useSite } from "../lib/site";
import { RagBadge } from "./Badges";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const STATUS = { complete: ["green", "Complete"], due_soon: ["amber", "Due soon"], expired: ["red", "Expired"], not_started: ["red", "Not started"] };
export const StatusBadge = ({ s }) => <RagBadge status={STATUS[s.status][0]} label={s.status === "complete" || s.status === "due_soon" ? `${STATUS[s.status][1]} - ${fmtDate(s.expires)}` : STATUS[s.status][1]} />;

export const saveBlob = async (url, name) => {
  try {
    const r = await api.get(url, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = name;
    a.click();
  } catch (e) { toast.error("Download failed"); }
};

const RolesDialog = ({ course, onClose, onSaved }) => {
  const [roles, setRoles] = useState(course.roles);
  const opts = ["all", ...Object.keys(ROLE_LABELS)];
  const toggle = (r) => setRoles(roles.includes(r) ? roles.filter((x) => x !== r) : [...roles, r]);
  const save = async () => {
    try { await api.put(`/training/courses/${course.id}/roles`, { roles }); toast.success("Assignment saved"); onSaved(); onClose(); } catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-slate-700 bg-ink-2" data-testid="course-roles-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl uppercase">Who must complete: {course.title}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-2">{opts.map((r) => <button key={r} onClick={() => toggle(r)} className={`rounded-md border px-3 py-1.5 text-sm ${roles.includes(r) ? "border-rose-500 bg-rose-500/20" : "border-slate-700 text-slate-400"}`} data-testid={`course-role-${r}`}>{r === "all" ? "Everyone" : ROLE_LABELS[r]}</button>)}</div>
        <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onClose}>Cancel</Button><Button className="bg-rose-600 hover:bg-rose-700" onClick={save} data-testid="course-roles-save">Save</Button></div>
      </DialogContent>
    </Dialog>
  );
};

const CourseCard = ({ c, canManage, canDeliver, onRoles, onDeliver }) => {
  const nav = useNavigate();
  return (
    <div className="panel flex min-w-0 flex-col p-4" data-testid={`course-card-${c.id}`}>
      <p className="eyebrow text-[10px]">{c.category}</p>
      <p className="mt-1 font-semibold leading-tight">{c.title}</p>
      <p className="mt-1 line-clamp-2 text-xs text-slate-400">{c.summary}</p>
      <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-slate-400">
        <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{c.duration_mins} min</span>
        <span className="flex items-center gap-1"><BookOpen className="h-3 w-3" />{c.lessons} lessons · {c.questions} questions</span>
        <span className="flex items-center gap-1"><Award className="h-3 w-3" />{c.validity_months ? `renew every ${c.validity_months} months` : "no expiry"}</span>
      </div>
      <p className="mt-1 text-[11px] text-slate-500">For: {c.roles.includes("all") ? "Everyone" : c.roles.map((r) => ROLE_LABELS[r] || r).join(", ") || "Not assigned"}</p>
      <div className="mt-2">{c.assigned_to_me || c.me.status !== "not_started" ? <StatusBadge s={c.me} /> : <span className="text-[11px] text-slate-500">Optional for you</span>}</div>
      <div className="mt-auto flex flex-wrap gap-2 pt-3">
        <Button size="sm" className="bg-rose-600 hover:bg-rose-700" onClick={() => nav(`/training/course/${c.id}`)} data-testid={`course-start-${c.id}`}>{c.me.status === "not_started" ? "Start course" : "Retake"}</Button>
        {c.me.completion_id && <Button size="sm" variant="outline" className="border-slate-700 bg-transparent" onClick={() => saveBlob(`/training/completions/${c.me.completion_id}/certificate`, `Certificate_${c.id}.pdf`)} data-testid={`course-cert-${c.id}`}>Certificate</Button>}
        {canDeliver && <Button size="sm" variant="outline" className="border-slate-700 bg-transparent" onClick={() => onDeliver(c)} data-testid={`course-deliver-${c.id}`}><Users className="mr-1 h-3.5 w-3.5" />Group session</Button>}
        {canManage && <Button size="sm" variant="ghost" onClick={() => onRoles(c)} data-testid={`course-assign-${c.id}`}><Settings2 className="h-4 w-4" /></Button>}
      </div>
    </div>
  );
};

export const CourseLibrary = () => {
  const { can } = useAuth();
  const { site } = useSite();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [cat, setCat] = useState("All");
  const [mine, setMine] = useState(false);
  const [roles, setRoles] = useState(null);
  const load = () => api.get("/training/courses").then((r) => setRows(r.data));
  useEffect(() => { if (site) load(); }, [site]); // eslint-disable-line react-hooks/exhaustive-deps
  const cats = useMemo(() => ["All", ...new Set(rows.map((c) => c.category))], [rows]);
  const shown = rows.filter((c) => (cat === "All" || c.category === cat) && (!mine || c.assigned_to_me));
  const todo = rows.filter((c) => c.assigned_to_me && ["not_started", "expired"].includes(c.me.status)).length;
  const deliver = async (c) => {
    try { const r = await api.post(`/training/courses/${c.id}/session`); toast.success(`${r.data.ref} created from course`); nav(`/training/${r.data.id}`); } catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <div className="space-y-4" data-testid="course-library">
      <div className="panel flex flex-wrap items-center gap-4 p-4">
        <div className="min-w-[220px] flex-1">
          <p className="font-semibold">{rows.length} ready-made courses</p>
          <p className="text-xs text-slate-400">Delivered on phone, tablet or PC: short lessons with read-aloud, a 5-question check (pass mark 80%), signed confirmation and a certificate. Refresher dates are set automatically. Supervisors can also run any course as a group session with signatures.</p>
        </div>
        <div className="text-right" data-testid="course-todo"><p className="font-display text-3xl font-bold text-rose-400">{todo}</p><p className="text-[11px] text-slate-400">courses you need to do</p></div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {cats.map((x) => <button key={x} onClick={() => setCat(x)} className={`rounded-md border px-2.5 py-1 text-xs ${cat === x ? "border-rose-500 bg-rose-500/20 text-rose-100" : "border-slate-700 text-slate-400"}`} data-testid={`course-cat-${x}`}>{x}</button>)}
        <label className="ml-auto flex items-center gap-2 text-xs text-slate-300"><input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} data-testid="course-mine-only" />Only courses assigned to me</label>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {shown.map((c) => <CourseCard key={c.id} c={c} canManage={can("training", "manage")} canDeliver={can("training", "manage")} onRoles={setRoles} onDeliver={deliver} />)}
      </div>
      {roles && <RolesDialog course={roles} onClose={() => setRoles(null)} onSaved={load} />}
    </div>
  );
};

export const TrainingMatrix = () => {
  const { site } = useSite();
  const [m, setM] = useState(null);
  const [cat, setCat] = useState("All");
  useEffect(() => { if (site) api.get("/training/matrix").then((r) => setM(r.data)); }, [site]);
  if (!m) return <p className="text-sm text-slate-500">Loading...</p>;
  const courses = m.courses.filter((c) => (cat === "All" || c.category === cat) && m.users.some((u) => u.courses[c.id]));
  const cell = { green: "bg-emerald-500/80", amber: "bg-amber-400", red: "bg-red-600/80" };
  return (
    <div className="space-y-3" data-testid="training-matrix">
      <div className="flex flex-wrap items-center gap-1.5">
        {["All", ...new Set(m.courses.map((c) => c.category))].map((x) => <button key={x} onClick={() => setCat(x)} className={`rounded-md border px-2.5 py-1 text-xs ${cat === x ? "border-rose-500 bg-rose-500/20" : "border-slate-700 text-slate-400"}`} data-testid={`matrix-cat-${x}`}>{x}</button>)}
        <span className="ml-auto flex gap-3 text-[11px] text-slate-400"><span><i className="mr-1 inline-block h-2.5 w-2.5 bg-emerald-500" />in date</span><span><i className="mr-1 inline-block h-2.5 w-2.5 bg-amber-400" />due in 30 days</span><span><i className="mr-1 inline-block h-2.5 w-2.5 bg-red-600" />missing / expired</span><span>blank = not required</span></span>
      </div>
      <div className="panel overflow-x-auto">
        <table className="text-xs">
          <thead><tr><th className="sticky left-0 bg-ink-2 px-3 py-2 text-left">Person</th><th className="px-2">%</th>{courses.map((c) => <th key={c.id} className="h-48 w-8 align-bottom" title={c.title}><div className="whitespace-nowrap pb-2 text-left font-normal text-slate-300" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", maxHeight: 180, overflow: "hidden", textOverflow: "ellipsis" }}>{c.title}</div></th>)}</tr></thead>
          <tbody>
            {m.users.map((u) => (
              <tr key={u.id} className="border-t border-slate-800" data-testid={`matrix-row-${u.id}`}>
                <td className="sticky left-0 whitespace-nowrap bg-ink-2 px-3 py-1.5"><p className="font-semibold">{u.name}</p><p className="text-[10px] text-slate-500">{ROLE_LABELS[u.role] || u.role}</p></td>
                <td className={`px-2 text-center font-mono ${u.compliance >= 90 ? "text-emerald-400" : u.compliance >= 60 ? "text-amber-300" : "text-red-400"}`}>{u.compliance}</td>
                {courses.map((c) => { const s = u.courses[c.id]; return <td key={c.id} className="p-0.5 text-center">{s && <span title={`${c.title}: ${STATUS[s.status][1]}${s.expires ? ` (${fmtDate(s.expires)})` : ""}`} className={`block h-6 w-7 rounded-sm ${cell[s.rag]}`} />}</td>; })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
